import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Users, CheckCircle, Clock, ChevronRight, Search, Loader2,
  Mail, Send, Plus, Copy, Link2, Trash2, AlertCircle, X
} from 'lucide-react';
import './ArtistInvites.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistInvites = () => {
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [publicArtists, setPublicArtists] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteForm, setInviteForm] = useState({ artist_name: '', email: '', public_artist_id: '' });

  const getToken = () => localStorage.getItem('adminToken');
  const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  useEffect(() => { fetchData(); fetchPublicArtists(); }, [filterStatus]);

  const fetchPublicArtists = async () => {
    try {
      const res = await fetch(`${API_URL}/api/artists`);
      if (res.ok) setPublicArtists(await res.json());
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/artists/invites${filterStatus ? `?status=${filterStatus}` : ''}`,
        { headers: getHeaders() }
      );
      if (res.ok) setInvites(await res.json());
    } catch {
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/artists/invites/${inviteId}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success('Invitation cancelled'); fetchData(); setSelectedItem(null); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed'); } finally { setActionLoading(false); }
  };

  const handleInviteArtist = async (e) => {
    e.preventDefault();
    if (!inviteForm.artist_name.trim() || !inviteForm.email.trim()) { toast.error('Fill in name and email'); return; }
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const payload = { artist_name: inviteForm.artist_name.trim(), email: inviteForm.email.trim(), ...(inviteForm.public_artist_id ? { public_artist_id: inviteForm.public_artist_id } : {}) };
      const res = await fetch(`${API_URL}/api/staff/artists/invite`, { method: 'POST', headers: getHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (res.ok) { setInviteResult(data); toast.success(`Invitation created for ${inviteForm.artist_name}!`); fetchData(); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Failed'); } finally { setInviteLoading(false); }
  };

  const copyLink = (url) => { if (url) { navigator.clipboard.writeText(url); toast.success('Link copied!'); } };

  const resetInviteForm = () => { setShowInviteModal(false); setInviteForm({ artist_name: '', email: '', public_artist_id: '' }); setInviteResult(null); };

  const filteredInvites = invites.filter(inv =>
    inv.artist_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'st-pending';
      case 'completed': return 'st-completed';
      case 'expired': return 'st-expired';
      default: return '';
    }
  };

  const BASE_URL = 'https://anyonemusicrecords.com';

  return (
    <div className="artist-invites-page" data-testid="artist-invites-page">
      <div className="aip-header">
        <div>
          <h1>Invite Links</h1>
          <p>Manage artist portal invitations</p>
        </div>
        <button className="aip-invite-btn" onClick={() => setShowInviteModal(true)} data-testid="invite-artist-btn">
          <Plus size={16} /> Invite Artist
        </button>
      </div>

      <div className="aip-toolbar">
        <div className="aip-search">
          <Search size={16} />
          <input type="text" placeholder="Search invites..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-input" />
        </div>
        <div className="aip-filters">
          {['', 'pending', 'completed', 'expired'].map(f => (
            <button key={f} className={`aip-filter-btn${filterStatus === f ? ' active' : ''}`} onClick={() => setFilterStatus(f)}>
              {f || 'All'}
            </button>
          ))}
        </div>
      </div>

      <div className="aip-split">
        {/* List */}
        <div className="aip-list">
          {loading ? (
            <div className="aip-empty"><Loader2 size={24} className="spinner" /><p>Loading...</p></div>
          ) : filteredInvites.length === 0 ? (
            <div className="aip-empty"><Send size={40} /><p>No invitations found</p></div>
          ) : (
            filteredInvites.map(inv => (
              <div
                key={inv.id}
                className={`aip-item${selectedItem?.id === inv.id ? ' selected' : ''}`}
                onClick={() => setSelectedItem(inv)}
                data-testid={`invite-item-${inv.id}`}
              >
                <div className="aip-item-info">
                  <h4>{inv.artist_name}</h4>
                  <span className="aip-item-email">{inv.email}</span>
                </div>
                <div className="aip-item-meta">
                  <span className={`aip-status ${getStatusClass(inv.status)}`}>{inv.status}</span>
                  <span className="aip-item-date">{formatDate(inv.created_at)}</span>
                </div>
                <ChevronRight size={16} className="aip-chevron" />
              </div>
            ))
          )}
        </div>

        {/* Detail */}
        <div className={`aip-detail${selectedItem ? ' open' : ''}`}>
          {selectedItem ? (
            <>
              <button className="aip-close" onClick={() => setSelectedItem(null)}><X size={18} /></button>
              <div className="aip-detail-header">
                <h2>{selectedItem.artist_name}</h2>
                <span className={`aip-status large ${getStatusClass(selectedItem.status)}`}>{selectedItem.status}</span>
              </div>

              <div className="aip-detail-card">
                <h3>Invite Details</h3>
                <div className="aip-info-row"><Mail size={14} /> <span>{selectedItem.email}</span></div>
                <div className="aip-info-row"><Send size={14} /> <span>Invited by: {selectedItem.invited_by}</span></div>
                <div className="aip-info-row"><Clock size={14} /> <span>Sent: {formatDate(selectedItem.created_at)}</span></div>
                <div className="aip-info-row"><AlertCircle size={14} /> <span>Expires: {formatDate(selectedItem.expires_at)}</span></div>
                {selectedItem.completed_at && <div className="aip-info-row"><CheckCircle size={14} /> <span>Registered: {formatDate(selectedItem.completed_at)}</span></div>}
                {(() => { const la = publicArtists.find(a => a.id === selectedItem.public_artist_id); return la ? <div className="aip-info-row"><Link2 size={14} /> <span>Linked: {la.name}</span></div> : null; })()}
              </div>

              {selectedItem.status === 'pending' && (
                <div className="aip-detail-card">
                  <h3>Registration Link</h3>
                  <div className="aip-link-box">
                    <input type="text" value={`${BASE_URL}/artist/register/${selectedItem.token}`} readOnly data-testid="detail-invite-link" />
                    <button onClick={() => copyLink(`${BASE_URL}/artist/register/${selectedItem.token}`)} data-testid="copy-invite-link"><Copy size={14} /></button>
                  </div>
                </div>
              )}

              {selectedItem.status === 'pending' && (
                <button className="aip-cancel-btn" onClick={() => handleCancelInvite(selectedItem.id)} disabled={actionLoading} data-testid="cancel-invite-btn">
                  {actionLoading ? <Loader2 size={16} className="spinner" /> : <Trash2 size={16} />}
                  Cancel Invitation
                </button>
              )}

              {selectedItem.status === 'completed' && <div className="aip-status-msg completed"><CheckCircle size={16} /> Artist has registered successfully</div>}
              {selectedItem.status === 'expired' && <div className="aip-status-msg expired"><AlertCircle size={16} /> This invitation has expired</div>}
            </>
          ) : (
            <div className="aip-empty-detail"><Send size={40} /><p>Select an invite to view details</p></div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="aip-modal-overlay" onClick={resetInviteForm}>
          <div className="aip-modal" onClick={e => e.stopPropagation()}>
            <div className="aip-modal-header">
              <h2><Plus size={18} /> Invite Artist</h2>
              <button onClick={resetInviteForm}><X size={18} /></button>
            </div>
            {inviteResult ? (
              <div className="aip-modal-success">
                <CheckCircle size={40} />
                <h3>Invitation Created!</h3>
                <p>Share this link with {inviteForm.artist_name}:</p>
                <div className="aip-link-box">
                  <input type="text" value={inviteResult.registration_url} readOnly data-testid="invite-link" />
                  <button onClick={() => copyLink(inviteResult.registration_url)}><Copy size={14} /></button>
                </div>
                <p className="aip-expires">Expires: {new Date(inviteResult.expires_at).toLocaleDateString()}</p>
                <button className="aip-invite-btn" onClick={resetInviteForm}>Done</button>
              </div>
            ) : (
              <form onSubmit={handleInviteArtist} className="aip-modal-form">
                <div className="aip-form-group">
                  <label>Artist Name *</label>
                  <input type="text" value={inviteForm.artist_name} onChange={e => setInviteForm({...inviteForm, artist_name: e.target.value})} placeholder="Artist or stage name" required data-testid="invite-artist-name" />
                </div>
                <div className="aip-form-group">
                  <label>Email *</label>
                  <input type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="artist@email.com" required data-testid="invite-email" />
                </div>
                <div className="aip-form-group">
                  <label><Link2 size={12} /> Link to Public Profile</label>
                  <select value={inviteForm.public_artist_id} onChange={e => setInviteForm({...inviteForm, public_artist_id: e.target.value})} data-testid="invite-link-profile">
                    <option value="">No linked profile</option>
                    {publicArtists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="aip-modal-actions">
                  <button type="button" onClick={resetInviteForm}>Cancel</button>
                  <button type="submit" className="aip-invite-btn" disabled={inviteLoading} data-testid="send-invite-btn">
                    {inviteLoading ? <Loader2 size={16} className="spinner" /> : <><Send size={16} /> Send Invite</>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistInvites;
